import { useMutation } from '@apollo/client';

import { logTapestryEvent } from 'logger';

import {
  SubmitMappingCorrections,
  SubmitMappingCorrectionsVariables,
} from './__generated__/operations';
import { SUBMIT_MAPPING_CORRECTIONS } from './operations';
import { extractTapestryErrorCode } from './useTapestryJobActions';
import { FieldsMap } from '../fieldMapping/MapRequiredFieldsCard';

/**
 * Invert the client `FieldsMap` ({ canonicalField: header }) into the
 * corrections shape the api merges onto `field_mapping`
 * ({ header: canonicalField }). Unmapped fields (empty header) are skipped so
 * we only send real assignments. Exported for unit testing.
 */
export function fieldsMapToCorrections(fieldsMap: FieldsMap): Record<string, string> {
  const corrections: Record<string, string> = {};
  Object.entries(fieldsMap).forEach(([field, header]) => {
    if (header) {
      corrections[header] = field;
    }
  });
  return corrections;
}

export interface UseSubmitMappingCorrectionsResult {
  submitMappingCorrections: (fileId: string, fieldsMap: FieldsMap) => Promise<unknown>;
  loading: boolean;
}

/**
 * Submits header→field corrections at the Tapestry mapping gate. The resolver
 * merges them onto the file's existing `field_mapping`, so callers only need to
 * send the full edited map; only changed/non-empty assignments are forwarded.
 */
export function useSubmitMappingCorrections(): UseSubmitMappingCorrectionsResult {
  const [mutate, { loading }] = useMutation<
    SubmitMappingCorrections,
    SubmitMappingCorrectionsVariables
  >(SUBMIT_MAPPING_CORRECTIONS);

  // Emit a TapestryAction event (latency + success/error + api error code) so
  // mapping-gate submissions are observable alongside the lifecycle mutations.
  const submitMappingCorrections = async (fileId: string, fieldsMap: FieldsMap) => {
    const start = performance.now();
    try {
      const result = await mutate({
        variables: { fileId, corrections: fieldsMapToCorrections(fieldsMap) },
      });
      logTapestryEvent('submit_mapping', { fileId, status: 'success' }, performance.now() - start);
      return result;
    } catch (error) {
      logTapestryEvent(
        'submit_mapping',
        { fileId, status: 'error', errorCode: extractTapestryErrorCode(error) ?? 'unknown' },
        performance.now() - start,
      );
      throw error;
    }
  };

  return { submitMappingCorrections, loading };
}
